import * as React from "react";
import {
  applyNatOverWireguard,
  applyWireguardOverNat,
  areNatAndWireguardFieldsVisible,
  NAT_OVER_WIREGUARD_MESSAGE,
  NatWireguardFieldUpdates,
  shouldPromptNatOverWireguard,
  shouldPromptWireguardOverNat,
  WIREGUARD_OVER_NAT_MESSAGE,
} from "./natWireguardConflict";
import { PatchObject, SiteInfo } from "./siteAboutInterfaces";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

type PendingAction =
  | { type: "wireguard"; wireguardId: number }
  | { type: "nat" };

type UseNatWireguardConflictDialogArgs = {
  siteInfo: SiteInfo;
  showWireguardFlag: boolean;
  enabled?: boolean;
  setSiteInfo: Dispatcher<SiteInfo>;
  setPatchObject: Dispatcher<PatchObject>;
  setChangesMade: (value: boolean) => void;
};

export const useNatWireguardConflictDialog = ({
  siteInfo,
  showWireguardFlag,
  enabled = true,
  setSiteInfo,
  setPatchObject,
  setChangesMade,
}: UseNatWireguardConflictDialogArgs) => {
  const [ecsNatInstance, setEcsNatInstance] = React.useState(
    siteInfo.ecs_nat_instance
  );
  const [wireguard, setWireguard] = React.useState(siteInfo.wireguard);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMessage, setDialogMessage] = React.useState("");
  const [pendingAction, setPendingAction] =
    React.useState<PendingAction | null>(null);
  const pendingActionRef = React.useRef<PendingAction | null>(null);
  const [wireguardAutocompleteKey, setWireguardAutocompleteKey] =
    React.useState(0);

  React.useEffect(() => {
    setEcsNatInstance(siteInfo.ecs_nat_instance);
    setWireguard(siteInfo.wireguard);
  }, [siteInfo.id]);

  React.useEffect(() => {
    pendingActionRef.current = pendingAction;
  }, [pendingAction]);

  const effectiveSiteInfo = React.useMemo(
    () => ({
      ...siteInfo,
      ecs_nat_instance: ecsNatInstance,
      wireguard,
    }),
    [siteInfo, ecsNatInstance, wireguard]
  );

  const bothFieldsVisible = areNatAndWireguardFieldsVisible(
    effectiveSiteInfo,
    showWireguardFlag
  );

  const applyFieldUpdates = React.useCallback(
    (updates: NatWireguardFieldUpdates) => {
      if (Object.prototype.hasOwnProperty.call(updates, "ecs_nat_instance")) {
        setEcsNatInstance(updates.ecs_nat_instance as boolean);
      }
      if (Object.prototype.hasOwnProperty.call(updates, "wireguard")) {
        setWireguard(updates.wireguard ?? null);
      }
      setPatchObject((previousState: PatchObject) => ({
        ...previousState,
        ...updates,
      }));
      setSiteInfo((previousState: SiteInfo) => ({
        ...previousState,
        ...updates,
      }));
      setChangesMade(true);
    },
    [setChangesMade, setPatchObject, setSiteInfo]
  );

  const handleWireguardSelection = React.useCallback(
    (wireguardId: number) => {
      if (
        enabled &&
        bothFieldsVisible &&
        shouldPromptWireguardOverNat(effectiveSiteInfo)
      ) {
        const action: PendingAction = { type: "wireguard", wireguardId };
        pendingActionRef.current = action;
        setPendingAction(action);
        setDialogMessage(WIREGUARD_OVER_NAT_MESSAGE);
        setDialogOpen(true);
        return;
      }

      applyFieldUpdates({ wireguard: wireguardId });
    },
    [applyFieldUpdates, bothFieldsVisible, effectiveSiteInfo, enabled]
  );

  const handleNatToggle = React.useCallback(
    (checked: boolean) => {
      if (
        enabled &&
        bothFieldsVisible &&
        shouldPromptNatOverWireguard(effectiveSiteInfo, checked)
      ) {
        const action: PendingAction = { type: "nat" };
        pendingActionRef.current = action;
        setPendingAction(action);
        setDialogMessage(NAT_OVER_WIREGUARD_MESSAGE);
        setDialogOpen(true);
        return;
      }

      applyFieldUpdates({ ecs_nat_instance: checked });
    },
    [applyFieldUpdates, bothFieldsVisible, effectiveSiteInfo, enabled]
  );

  const handleConfirm = React.useCallback(() => {
    const action = pendingActionRef.current;
    if (!action) {
      setDialogOpen(false);
      return;
    }

    if (action.type === "wireguard") {
      applyFieldUpdates(applyWireguardOverNat(action.wireguardId));
    } else {
      applyFieldUpdates(applyNatOverWireguard());
    }

    pendingActionRef.current = null;
    setPendingAction(null);
    setDialogOpen(false);
  }, [applyFieldUpdates]);

  const handleCancel = React.useCallback(() => {
    if (pendingActionRef.current?.type === "wireguard") {
      setWireguardAutocompleteKey((key) => key + 1);
    }
    pendingActionRef.current = null;
    setPendingAction(null);
    setDialogOpen(false);
  }, []);

  return {
    ecsNatInstance,
    wireguard,
    wireguardAutocompleteKey,
    handleWireguardSelection,
    handleNatToggle,
    conflictDialogProps: {
      open: dialogOpen,
      message: dialogMessage,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
};
